import React, { useState } from "react";
import AdminApiTable from "@/modules/racket_lab_admin/AdminApiTable";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

/**
 * RLInventoryMonthlyReport — SS-6.2 (Jun 2026).
 *
 * READ-ONLY monthly consumption report fed by
 * `GET /admin/racket-lab/inventory/_reports/monthly-consumption`.
 * Filters: year / month / shop (server-side scoped by RBAC).
 * Blocks: summary tiles · most-consumed items · per-shop table ·
 * latest consumption movements. QA universe shown separately.
 * Zero writes, zero notifications — admin-only, preview-first.
 */
const INPUT_CLS = "rounded-md border border-slate-300 px-2 py-1.5 text-sm";
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const SummaryTile = ({ label, value, testId }) => (
  <div data-testid={testId} className="rounded-md border border-slate-200 bg-white p-2">
    <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-base font-semibold text-slate-900">{value ?? "—"}</p>
  </div>
);

const RLInventoryMonthlyReport = ({ shops, shopName }) => {
  const t = useRacketLabAdminT();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [shop, setShop] = useState("");

  const params = { year, month };
  if (shop) params.racket_lab_shop_id = shop;
  const { data, loading, error } = useAdminApi(
    "/admin/racket-lab/inventory/_reports/monthly-consumption", { params },
  );
  const s = data?.summary;
  const years = [];
  for (let y = now.getUTCFullYear(); y >= 2025; y -= 1) years.push(y);

  return (
    <section data-testid="rl-inv-report-section" className="flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{t("inventory.report.title")}</h2>
          <p className="text-[10px] text-slate-500">{t("inventory.report.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select data-testid="rl-inv-report-month" value={month} onChange={(e) => setMonth(parseInt(e.target.value, 10))} className={INPUT_CLS}>
            {MONTHS.map((m) => (<option key={m} value={m}>{t(`inventory.report.months.${m}`)}</option>))}
          </select>
          <select data-testid="rl-inv-report-year" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className={INPUT_CLS}>
            {years.map((y) => (<option key={y} value={y}>{y}</option>))}
          </select>
          <select data-testid="rl-inv-report-shop" value={shop} onChange={(e) => setShop(e.target.value)} className={INPUT_CLS}>
            <option value="">{t("inventory.report.all_shops")}</option>
            {shops.map((sh) => (<option key={sh.id} value={sh.id}>{sh.name || sh.id}</option>))}
          </select>
        </div>
      </div>

      {loading && <p className="text-xs text-slate-500">{t("inventory.report.loading")}</p>}
      {!loading && error && (
        <p data-testid="rl-inv-report-error" className="text-xs text-rose-700">
          {`${t("dashboard.error_prefix")} ${error.status || ""}`}
        </p>
      )}

      {!loading && !error && s && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
            <SummaryTile label={t("inventory.report.units_consumed")} value={s.units_consumed} testId="rl-inv-report-units" />
            <SummaryTile label={t("inventory.report.net_units")} value={s.net_units_consumed} testId="rl-inv-report-net" />
            <SummaryTile label={t("inventory.report.movements_count")} value={s.consumption_movements} testId="rl-inv-report-movs" />
            <SummaryTile label={t("inventory.report.compensated")} value={s.compensated_units} testId="rl-inv-report-compensated" />
            <SummaryTile label={t("inventory.report.tickets_closed")} value={s.tickets_closed} testId="rl-inv-report-tickets-closed" />
            <SummaryTile label={t("inventory.report.with_consumption")} value={s.tickets_with_consumption} testId="rl-inv-report-with-consumption" />
            <SummaryTile label={t("inventory.report.coverage")} value={s.coverage_pct != null ? `${s.coverage_pct}%` : "—"} testId="rl-inv-report-coverage" />
            <SummaryTile label={t("inventory.report.cost")} value={s.estimated_cost != null ? `${s.estimated_cost} €` : "—"} testId="rl-inv-report-cost" />
            <SummaryTile label={t("inventory.report.net_cost")} value={s.net_estimated_cost != null ? `${s.net_estimated_cost} €` : "—"} testId="rl-inv-report-net-cost" />
            <SummaryTile label={t("inventory.report.qa_closed")} value={s.qa_tickets_closed} testId="rl-inv-report-qa-closed" />
          </div>
          {s.items_without_cost > 0 ? (
            <p data-testid="rl-inv-report-missing-cost-note" className="text-[10px] text-amber-700">
              {t("inventory.report.missing_cost_note", { n: String(s.items_without_cost) })}
            </p>
          ) : null}
          <p className="text-[10px] text-slate-400">{t("inventory.report.qa_note")}</p>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <div>
              <h3 className="mb-1 text-xs font-semibold text-slate-700">{t("inventory.report.top_items_title")}</h3>
              <AdminApiTable
                columns={[
                  { key: "item", label: t("inventory.columns.item"), render: (r) => (
                    <span className="text-xs font-medium">{r.brand_label || "—"} {r.model_label || r.inventory_item_id?.slice(0, 8)}</span>
                  )},
                  { key: "type", label: t("inventory.columns.type"), render: (r) => (r.item_type ? t(`inventory.types.${r.item_type}`) : "—") },
                  { key: "units", label: t("inventory.report.units_consumed"), render: (r) => (
                    <span className="font-semibold">{r.units_consumed} {r.unit ? t(`inventory.units.${r.unit}`) : ""}</span>
                  )},
                  { key: "cost", label: t("inventory.report.cost"), render: (r) => (
                    <span>{r.estimated_cost != null ? `${r.estimated_cost} €` : "—"}</span>
                  )},
                  { key: "movs", label: t("inventory.report.movements_count"), render: (r) => r.movements },
                ]}
                rows={data.top_items || []} loading={false} error={null}
                emptyLabel={t("inventory.report.empty")} dataTestId="rl-inv-report-top-items"
              />
            </div>
            <div>
              <h3 className="mb-1 text-xs font-semibold text-slate-700">{t("inventory.report.by_shop_title")}</h3>
              <AdminApiTable
                columns={[
                  { key: "shop", label: t("inventory.columns.shop"), render: (r) => (
                    <span className="text-xs">{shopName[r.racket_lab_shop_id] || r.racket_lab_shop_id?.slice(0, 12)}</span>
                  )},
                  { key: "units", label: t("inventory.report.units_consumed"), render: (r) => r.units_consumed },
                  { key: "cost", label: t("inventory.report.cost"), render: (r) => (
                    <span>{r.estimated_cost != null ? `${r.estimated_cost} €` : "—"}</span>
                  )},
                  { key: "tw", label: t("inventory.report.with_consumption"), render: (r) => r.tickets_with_consumption },
                  { key: "tc", label: t("inventory.report.tickets_closed"), render: (r) => r.tickets_closed },
                ]}
                rows={data.by_shop || []} loading={false} error={null}
                emptyLabel={t("inventory.report.empty")} dataTestId="rl-inv-report-by-shop"
              />
            </div>
          </div>

          <div>
            <h3 className="mb-1 text-xs font-semibold text-slate-700">{t("inventory.report.movements_title")}</h3>
            <AdminApiTable
              columns={[
                { key: "at", label: t("inventory.movements.columns.at"), render: (m) => (
                  <span className="text-[10px] text-slate-500">{(m.created_at || "").slice(0, 16).replace("T", " ")}</span>
                )},
                { key: "shop", label: t("inventory.columns.shop"), render: (m) => (
                  <span className="text-xs">{shopName[m.racket_lab_shop_id] || m.racket_lab_shop_id?.slice(0, 12)}</span>
                )},
                { key: "delta", label: t("inventory.movements.columns.delta"), render: (m) => (
                  <span className="text-rose-700">{m.delta}</span>
                )},
                { key: "ticket", label: t("inventory.report.ticket"), render: (m) => (
                  <span className="text-[10px] text-slate-500">{m.ticket_id ? `#${m.ticket_id.slice(0, 8)}` : "—"}</span>
                )},
              ]}
              rows={data.recent_movements || []} loading={false} error={null}
              emptyLabel={t("inventory.report.empty")} dataTestId="rl-inv-report-movements"
            />
          </div>
        </>
      )}
    </section>
  );
};

export default RLInventoryMonthlyReport;
